(() => {
  if (customElements.get('tz-transition')) return;
  class TransitionBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.root = window.Shopify?.routes?.root || '/';
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency });
      this.rows = [...this.querySelectorAll('[data-item]')].map(node => ({ node, key: node.dataset.item, title: node.dataset.title, include: node.querySelector('[data-include]'), select: node.querySelector('[data-size]') }));
      this.presets = { mild: ['pants', 'shirt'], cool: ['pants', 'shirt', 'hoodie'], cold: ['pants', 'shirt', 'parka'], pants_only: ['pants'] };
      this.configuration = 'mild';
      this.busy = false;
      this.succeeded = false;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('change', event => {
        if (event.target.matches('input[type=radio]')) this.setPreset(event.target.value);
        else if (event.target.matches('[data-include]')) {
          this.configuration = 'custom';
          this.querySelectorAll('input[type=radio]').forEach(radio => { radio.checked = false; });
        }
        if (event.target.matches('[data-size]')) event.target.removeAttribute('aria-invalid');
        this.clearResult();
        this.render();
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.matches('[data-pants-only]')) { this.setPreset('pants_only'); this.clearResult(); this.render(); }
        if (button.matches('[data-add]')) this.add(button);
        if (button.matches('[data-fit]')) this.querySelector(`#${CSS.escape(button.dataset.fit)}`).showModal();
        if (button.matches('[data-close]')) button.closest('dialog').close();
      }, options);
      document.addEventListener('click', event => {
        const anchor = event.target.closest('a[href="#transition-builder"]');
        if (!anchor) return;
        event.preventDefault();
        this.querySelector('#transition-builder').scrollIntoView({ block: 'start', behavior: this.motion() });
        this.querySelector('input[type=radio]').focus({ preventScroll: true });
      }, options);
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {
          const box = dialog.getBoundingClientRect();
          if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dialog.close();
        }, options);
      });
      this.observe = new MutationObserver(() => this.mobile());
      this.observe.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      const drawer = document.querySelector('cart-drawer');
      if (drawer) this.observe.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this.querySelectorAll('dialog').forEach(dialog => this.observe.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
      window.addEventListener('scroll', () => this.mobile(), { ...options, passive: true });
      window.addEventListener('resize', () => this.mobile(), options);
      window.visualViewport?.addEventListener('resize', () => this.mobile(), options);
      document.addEventListener('focusin', () => this.mobile(), options);
      document.addEventListener('focusout', () => requestAnimationFrame(() => this.mobile()), options);
      this.render();
    }
    disconnectedCallback() { this.abort?.abort(); this.observe?.disconnect(); this.initialized = false; }
    text(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), this.labels[key] || key); }
    format(cents) { return this.money.format(cents / 100); }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    selected() { return this.rows.filter(row => row.include.checked); }
    price(row) { return Number(row.select.selectedOptions[0]?.dataset.price || row.node.dataset.min); }
    missing() { return this.selected().filter(row => !row.select.value || row.select.selectedOptions[0]?.disabled || row.node.dataset.available !== 'true'); }
    discount() {
      const selected = this.selected();
      const pants = selected.find(row => row.key === 'pants');
      const parka = selected.find(row => row.key === 'parka');
      return pants && parka ? Math.min(Number(this.dataset.offer), this.price(pants) + this.price(parka)) : 0;
    }
    total() { return this.selected().reduce((sum, row) => sum + this.price(row), 0) - this.discount(); }
    setPreset(key) {
      if (this.busy || !this.presets[key]) return;
      this.configuration = key;
      this.rows.forEach(row => { row.include.checked = this.presets[key].includes(row.key); });
      this.querySelectorAll('input[type=radio]').forEach(radio => { radio.checked = radio.value === key; });
    }
    clearResult() {
      this.succeeded = false;
      this.querySelector('[data-success]').hidden = true;
      this.querySelector('[data-status]').textContent = '';
      this.querySelector('[data-cart-link]').hidden = true;
    }
    render() {
      const selected = this.selected();
      const missing = this.missing();
      const total = this.format(this.total());
      const count = selected.length;
      this.rows.forEach(row => {
        row.node.dataset.selected = String(row.include.checked);
        row.node.querySelector('[data-controls]').hidden = !row.include.checked;
        row.node.querySelector('[data-inclusion]').textContent = row.node.dataset.available !== 'true' ? this.text('not_available') : this.text(row.include.checked ? 'included' : 'optional');
        row.node.querySelector('[data-row-price]').textContent = this.format(this.price(row));
        row.include.disabled = this.busy || row.key === 'pants' || row.node.dataset.available !== 'true';
        row.select.disabled = this.busy;
      });
      this.querySelectorAll('input[type=radio],[data-pants-only]').forEach(control => { control.disabled = this.busy; });
      const summary = this.querySelector('[data-summary]');
      summary.replaceChildren(...selected.map(row => {
        const li = document.createElement('li');
        const title = document.createElement('span');
        title.textContent = row.title;
        const size = document.createElement('small');
        size.textContent = row.select.value ? row.select.selectedOptions[0].textContent : this.text('choose_size');
        title.append(size);
        const price = document.createElement('b');
        price.textContent = this.format(this.price(row));
        li.append(title, price);
        return li;
      }));
      this.querySelector('[data-setup-label]').textContent = this.text(this.configuration);
      this.querySelector('[data-total]').textContent = this.text(count === 1 ? 'item_total' : 'items_total', { count, total });
      this.querySelector('[data-readiness]').textContent = missing.length ? this.text(missing.length === 1 ? 'missing_one' : 'missing', { count: missing.length }) : this.text('ready');
      this.querySelector('[data-offer-line]').hidden = !this.discount();
      this.querySelector('[data-offer-terms]').hidden = !this.discount();
      this.querySelector('[data-savings]').textContent = `−${this.format(this.discount())}`;
      this.querySelectorAll('[data-add]').forEach(button => {
        button.textContent = this.busy ? this.text('adding') : this.succeeded ? this.text('add_more') : missing.length ? this.text('choose_sizes') : this.text(count === 1 ? 'add_one' : 'add', { count, total });
        button.disabled = this.busy;
        button.setAttribute('aria-busy', String(this.busy));
      });
      this.querySelector('[data-mobile-summary]').textContent = this.succeeded ? this.text('success') : `${count} · ${total}`;
      this.querySelector('[data-mobile] [data-add]').hidden = this.succeeded;
      this.querySelector('[data-mobile-checkout]').hidden = !this.succeeded;
      this.mobile();
    }
    mobile() {
      const viewport = window.visualViewport;
      const focusedInput = document.activeElement?.matches('textarea,input:not([type=radio]):not([type=checkbox])');
      const overlay = document.querySelector('cart-drawer.active,dialog[open],details.menu-opening');
      const keyboard = viewport && window.innerHeight - viewport.height > 120;
      const aboveBuilder = this.querySelector('#transition-builder').getBoundingClientRect().top > window.innerHeight;
      this.querySelector('[data-mobile]').hidden = Boolean(overlay || keyboard || focusedInput || aboveBuilder);
    }
    focusMissing() {
      const missing = this.missing();
      missing.forEach(row => row.select.setAttribute('aria-invalid', 'true'));
      const row = missing[0];
      if (!row) return;
      const parent = row.node.closest('details');
      if (parent) parent.open = true;
      row.node.scrollIntoView({ block: 'center', behavior: this.motion() });
      row.select.focus({ preventScroll: true });
    }
    async json(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: AbortSignal.timeout(20000) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.text('error'));
      return result;
    }
    async add(button) {
      if (this.busy) return;
      if (this.succeeded) { this.clearResult(); this.render(); return; }
      if (this.missing().length) { this.focusMissing(); return; }
      const selected = this.selected();
      const items = selected.map(row => ({ id: Number(row.select.value), quantity: 1 }));
      const addedText = selected.map(row => `${row.title} — ${row.select.selectedOptions[0].textContent} × 1`);
      this.busy = true;
      this.clearResult();
      this.render();
      let deferred;
      let added = false;
      let submitted = false;
      const LinesEvent = window.StandardEvents?.CartLinesUpdateEvent;
      try {
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender().map(section => section.id) || [];
        if (LinesEvent) {
          deferred = LinesEvent.createPromise();
          deferred.promise.catch(() => {});
          button.dispatchEvent(new LinesEvent({ action: 'add', context: 'product', lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
        }
        submitted = true;
        const result = await this.json(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: location.pathname }) });
        added = true;
        this.succeeded = true;
        const list = this.querySelector('[data-added-list]');
        list.replaceChildren(...addedText.map(text => { const li = document.createElement('li'); li.textContent = text; return li; }));
        this.querySelector('[data-success]').hidden = false;
        this.querySelector('[data-cart-link]').hidden = false;
        const cart = await this.json(`${this.root}cart.js`);
        deferred?.resolve({ cart: LinesEvent.createCartFromAjaxResponse(cart) });
        if (drawer) {
          if (sections.some(id => !result.sections?.[id])) {
            const url = new URL(location.href);
            url.searchParams.set('sections', sections.join(','));
            result.sections = await this.json(url.href);
          }
          if (sections.some(id => !result.sections?.[id])) throw new Error(this.text('refresh_error'));
          drawer.setActiveElement(button);
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'treezyn-transition-builder', cartData: result });
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'treezyn-transition-builder' } }));
      } catch (error) {
        deferred?.reject(error);
        this.querySelector('[data-cart-link]').hidden = !submitted;
        this.querySelector('[data-status]').textContent = added ? this.text('refresh_error') : (error.name === 'TypeError' || error.name === 'TimeoutError') ? this.text('error') : error.message || this.text('error');
        if (submitted && !added) document.dispatchEvent(new CustomEvent('treezyn:cart:rewards-retry'));
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }
  customElements.define('tz-transition', TransitionBuilder);
})();
